import { Data } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { formatOrgNr } from '@nk/company-block';
import {
  CompanyDetailsEntity,
  CompanyEntity,
  CustomerPersonEntity,
  EntityMatchDecisionTypeKey,
  PersonEntity,
  ScreeningCaseCompositeEntity,
  ScreeningCaseEntity,
  ScreeningCaseReviewEntity,
  ScreeningEntityScreeningCaseStateEntity,
  WithUuid,
} from '@nk/data-entities-lib';
import { GenericAddress } from '@nk/geo-block';
import { isPersonLikeEntity } from '@nk/person-block';
import { titleCaseCompanyName } from '@nk/utils-lib';
import * as dayjs from 'dayjs';
import { CustomerPersonRouteData } from './screening-customer-person-import.utils';
import { SCREENING_STATUS_TAGS_TRANSLATIONS } from './screening-overview.utils';

export const SCREENING_CASE_REVIEW_ROUTE = 'screening-case/:screeningCaseUuid';

export const ENTITY_MATCH_DECISION_TYPE_TRANSLATIONS: Record<EntityMatchDecisionTypeKey, string> = {
  [EntityMatchDecisionTypeKey.good_match]: extractTranslation(
    'screening.case.forms.entity_match_decision_type.good_match',
  ),
  [EntityMatchDecisionTypeKey.bad_match]: extractTranslation(
    'screening.case.forms.entity_match_decision_type.bad_match',
  ),
  [EntityMatchDecisionTypeKey.bad_match_dont_match_again]: extractTranslation(
    'screening.case.forms.entity_match_decision_type.bad_match_dont_match_again',
  ),
};

export function getLatestScreeningCaseEntity(items: ScreeningCaseEntity[]): ScreeningCaseEntity | null {
  if (!(items && items.length)) {
    return null;
  }
  return items.sort((a, b) => dayjs(b.created_timestamp).valueOf() - dayjs(a.created_timestamp).valueOf())[0];
}

export function getLatestScreeningCaseCompositeEntity<
  T extends { screening_case_composites: ScreeningCaseCompositeEntity[] },
>(items: T['screening_case_composites']): ScreeningCaseCompositeEntity | null {
  if (!(items && items.length)) {
    return null;
  }
  return items.sort(
    (a, b) => dayjs(b.screening_case.created_timestamp).valueOf() - dayjs(a.screening_case.created_timestamp).valueOf(),
  )[0];
}

export function getScreeningCaseReviewNavigateBackRoute(data: Data): string[] {
  if ('navigationOnFormComplete' in data) {
    if (data['navigationOnFormComplete'] === 'screening-customer-person-view') {
      const resolvedInfo = data['resolvedInfo'] as CustomerPersonRouteData;
      const customerPersonUuid = resolvedInfo.entityData.uuid ?? '';
      return ['/screening', 'customer-person', customerPersonUuid];
    }
    if (data['navigationOnFormComplete'] === 'screening-company-view') {
      const resolvedInfo = data['resolvedInfo'] as {
        entityData: CompanyEntity;
      };
      const companyUuid = resolvedInfo.entityData.org_nr && resolvedInfo.entityData.uuid;
      if (companyUuid) {
        return ['/company', companyUuid, 'screening'];
      }
    }
  }
  return ['/'];
}

export type ScreeningCaseReviewFormValue = Required<
  Pick<
    ScreeningCaseReviewEntity,
    'entity_match_decision_type_uuid' | 'pep_confirmed_flag' | 'screening_case_review_comment'
  >
>;

export interface GenericPersonEntity {
  person: {
    uuid: string;
    person_master_uuid: string | null;
    full_name: string;
    birth_date: Date | null;
    birth_years: number[] | null;
    gender_uuid: string;
    addresses: GenericAddress[];
  };
}

export interface GenericCompanyEntity {
  company: {
    uuid: string;
    company_name: string;
    establishment_dates: number[] | null;
    addresses: GenericAddress[];
  };
}

function getPersonAddress(person: PersonEntity): GenericAddress {
  return {
    street_address: person.street_address,
    postal_code: person.postal_code,
    country_name: person.country_name,
    postal_place: person.postal_place,
  };
}

function getCompanyAddress(company: CompanyDetailsEntity): GenericAddress {
  return {
    street_address: company.postal_address_street,
    postal_code: company.postal_address_postal_code,
    country_name: company.postal_address_country,
    postal_place: company.postal_address_place,
  };
}

export function isGenericPersonEntity(item: unknown): item is GenericPersonEntity {
  return !!(item as GenericPersonEntity)?.person;
}

export function isGenericCompanyEntity(item: unknown): item is GenericCompanyEntity {
  return !!(item as GenericCompanyEntity)?.company;
}

export function getGenericPerson(
  personEntity: WithUuid<PersonEntity> | WithUuid<CustomerPersonEntity>,
): GenericPersonEntity {
  return {
    person: {
      uuid: personEntity.uuid,
      person_master_uuid: isPersonLikeEntity(personEntity) ? personEntity.person_master_uuid : null,
      full_name: personEntity.full_name,
      birth_date: personEntity.birth_date instanceof Date ? personEntity.birth_date : null,
      birth_years: personEntity.birth_date instanceof Date ? [personEntity.birth_date.getFullYear()] : null,
      gender_uuid: personEntity.gender_uuid,
      addresses: isPersonLikeEntity(personEntity) ? [getPersonAddress(personEntity)] : [],
      // national_identification_schema: geoCountryFlagDict[item.monitoring_customer_person.country_code_uuid],
    },
  };
}

export function getGenericCompany(companyEntity: CompanyDetailsEntity): GenericCompanyEntity {
  return {
    company: {
      uuid: companyEntity.company_uuid,
      company_name: `${titleCaseCompanyName(companyEntity.name)} (${formatOrgNr(
        companyEntity.org_nr,
        companyEntity.org_nr_schema,
      )})`,
      establishment_dates:
        companyEntity.establishment_date instanceof Date ? [companyEntity.establishment_date.getFullYear()] : null,
      addresses: companyEntity ? [getCompanyAddress(companyEntity)] : [],
      // national_identification_schema: geoCountryFlagDict[item.monitoring_customer_person.country_code_uuid],
    },
  };
}

export function getScreeningCaseReviewTitle(
  state: ScreeningEntityScreeningCaseStateEntity | undefined,
  translateService: TranslateService,
): string {
  if (!state) return '';

  const caseReviewedText = extractTranslation('screening.case.review.case_reviewed_title');
  const casePendingText = extractTranslation('screening.case.review.case_pending_title');

  if (state.pep_flag) {
    return (
      translateService.instant(caseReviewedText) +
      ': ' +
      translateService.instant(SCREENING_STATUS_TAGS_TRANSLATIONS.pep_flag)
    );
  }
  if (state.sanction_flag) {
    return (
      translateService.instant(caseReviewedText) +
      ': ' +
      translateService.instant(SCREENING_STATUS_TAGS_TRANSLATIONS.sanction_flag)
    );
  }
  if (state.sanction_match_declined_flag) {
    return (
      translateService.instant(caseReviewedText) +
      ': ' +
      translateService.instant(SCREENING_STATUS_TAGS_TRANSLATIONS.sanction_match_declined_flag)
    );
  }
  if (state.possible_pep_flag) {
    return (
      translateService.instant(casePendingText) +
      ': ' +
      translateService.instant(SCREENING_STATUS_TAGS_TRANSLATIONS.possible_pep_flag)
    );
  }
  if (state.possible_sanction_flag) {
    return (
      translateService.instant(casePendingText) +
      ': ' +
      translateService.instant(SCREENING_STATUS_TAGS_TRANSLATIONS.possible_sanction_flag)
    );
  }
  if (state.pep_declined_flag) {
    return (
      translateService.instant(caseReviewedText) +
      ': ' +
      translateService.instant(SCREENING_STATUS_TAGS_TRANSLATIONS.pep_declined_flag)
    );
  }
  if (state.pep_match_declined_flag) {
    return (
      translateService.instant(caseReviewedText) +
      ': ' +
      translateService.instant(SCREENING_STATUS_TAGS_TRANSLATIONS.pep_match_declined_flag)
    );
  }
  return '';
}
